import type { ApolloClient } from '@apollo/client';
import { preOpBoardSurgeonReadyEvents } from '@/graph/patients';

export type PreOpBoardReadyEvent = { eventId: string; patientId: string | number };
const MAX_SEEN_EVENTS = 1024;

// Event IDs survive retries/reconnects; patient IDs deliberately do not suppress
// a later Clear -> Ready transition for the same visit.
export const subscribeToPreOpBoardSound = (
  client: Pick<ApolloClient<object>, 'subscribe'>,
  onReady: () => void,
  seenEvents: Set<string> = new Set()
): (() => void) => {
  let disposed = false;
  let generation = 0;
  let retryDelay = 1000;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let subscription: { unsubscribe: () => void } | undefined;

  const retry = () => {
    if (disposed || retryTimer !== undefined) return;
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      subscription?.unsubscribe();
      connect();
    }, retryDelay);
    retryDelay = Math.min(retryDelay * 2, 30000);
  };

  const connect = () => {
    const currentGeneration = ++generation;
    subscription = client
      .subscribe<{ event: PreOpBoardReadyEvent | null }>({
        query: preOpBoardSurgeonReadyEvents,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
        context: { queryDeduplication: false },
      })
      .subscribe({
        next: ({ data, errors }) => {
          if (disposed || currentGeneration !== generation || errors?.length || !data) return;
          retryDelay = 1000;
          const event = data.event;
          // The server sends null when the stream starts, never historical Ready patients.
          if (!event?.eventId || seenEvents.has(event.eventId)) return;
          seenEvents.add(event.eventId);
          if (seenEvents.size > MAX_SEEN_EVENTS) seenEvents.delete(seenEvents.values().next().value!);
          onReady();
        },
        error: retry,
        complete: retry,
      });
  };

  connect();
  return () => {
    disposed = true;
    generation++;
    if (retryTimer !== undefined) clearTimeout(retryTimer);
    subscription?.unsubscribe();
  };
};
